import { Handle, Position } from '@xyflow/react'
import { Share2, ArrowDown, ArrowUp, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { GraphNodeData } from '@/types/graph'
import { useCanvasStore } from '@store/canvas'

interface IONodeProps {
  data: GraphNodeData
  selected?: boolean
  id: string
}

export default function IONode({ data, selected, id }: IONodeProps) {
  const { t } = useTranslation()
  const removeNode = useCanvasStore((state) => state.removeNode)

  const direction = String(data.config?.direction || 'io').toLowerCase()
  const isInput = direction === 'input'
  const isOutput = direction === 'output'

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    removeNode(id)
  }

  return (
    <div
      className={`px-4 py-3 rounded-lg border-2 bg-white shadow-md min-w-44 transition-all ${
        selected
          ? 'border-agent-indigo ring-2 ring-agent-indigo/30 shadow-lg'
          : 'border-agent-indigo/50 hover:border-agent-indigo'
      }`}
    >
      <Handle
        type="target"
        position={Position.Top}
      />

      {/* Header */}
      <div className="flex items-center justify-between mb-2 group">
        <div className="flex items-center gap-2 flex-1">
          {isInput && <ArrowDown size={16} className="text-agent-indigo" />}
          {isOutput && <ArrowUp size={16} className="text-agent-indigo" />}
          {!isInput && !isOutput && (
            <Share2 size={16} className="text-agent-indigo" />
          )}
          <div className="font-bold text-sm text-gray-900 truncate">
            {data.label || (isInput ? t('build.inputLabel') : isOutput ? t('build.outputLabel') : 'IO')}
          </div>
        </div>
        <button
          onClick={handleDelete}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-100 rounded text-red-600"
          title={t('build.deleteNode')}
        >
          <X size={14} />
        </button>
      </div>

      {/* Format badge */}
      {data.config?.format && (
        <div className="mb-2">
          <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs font-medium">
            {String(data.config.format)}
          </span>
        </div>
      )}

      {/* Description */}
      {data.config?.description && (
        <div className="text-xs text-gray-600 bg-gray-50 rounded p-2 mb-2">
          {String(data.config.description)}
        </div>
      )}

      <Handle
        type="source"
        position={Position.Bottom}
      />
    </div>
  )
}
